import { useScrollReveal } from '@/hooks/useScrollReveal';

interface ProductFeaturesProps {
  tags: string[];
}

const featureMap: Record<string, { icon: string; title: string; desc: string }> = {
  '隱形': { icon: 'ri-eye-off-line', title: '隱形設計', desc: '隱藏於耳道深處，旁人幾乎無法察覺' },
  'AI降噪': { icon: 'ri-sound-module-line', title: 'AI 智能降噪', desc: '自動識別環境噪音，讓對話更清晰自然' },
  '防水': { icon: 'ri-drop-line', title: '防水防汗', desc: 'IP57 防護等級，運動雨天都安心' },
  '智能調節': { icon: 'ri-cpu-line', title: '環境智能調節', desc: '內建環境分析系統，自動優化音質設定' },
  '藍牙': { icon: 'ri-bluetooth-line', title: '藍牙連線', desc: '支援藍牙串流，直接接聽電話與音樂' },
  'App控制': { icon: 'ri-smartphone-line', title: 'App 控制', desc: '透過手機 App 即時調整音質與模式' },
  '充電式': { icon: 'ri-battery-charge-line', title: '充電設計', desc: '充電盒自動充電，告別換電池困擾' },
  '長續航': { icon: 'ri-time-line', title: '長效續航', desc: '一次充電可使用一整天，無後顧之憂' },
  '入門首選': { icon: 'ri-star-smile-line', title: '簡易入門', desc: '操作簡單，長輩也能輕鬆上手' },
  '迷你': { icon: 'ri-focus-3-line', title: '極致迷你', desc: '業界最小尺寸，輕巧無負擔' },
  '輕巧': { icon: 'ri-leaf-line', title: '輕盈舒適', desc: '重量僅 1.8 克，長時間佩戴不疲勞' },
  '舒適': { icon: 'ri-heart-pulse-line', title: '舒適佩戴', desc: '人體工學設計，柔軟耳模貼合耳型' },
  '耐用': { icon: 'ri-shield-check-line', title: '堅固耐用', desc: '高品質材質，經久耐用' },
  '防汗': { icon: 'ri-drop-line', title: '防汗設計', desc: '防汗材質適合運動與戶外活動' },
  '藍牙5.0': { icon: 'ri-bluetooth-line', title: '藍牙 5.0', desc: '最新藍牙技術，連線穩定低延遲' },
  '串流音訊': { icon: 'ri-music-2-line', title: '串流音訊', desc: '支援雙耳同步串流，立體聲效果' },
  '雙耳同步': { icon: 'ri-headphone-line', title: '雙耳同步', desc: '左右耳同步處理，聲音定位更精準' },
  '兒童專用': { icon: 'ri-parent-line', title: '兒童專用', desc: '專為兒童設計，成長型調節功能' },
  '耐摔': { icon: 'ri-shield-check-line', title: '耐摔設計', desc: '堅固外殼，經得起兒童活潑好動' },
  '色彩豐富': { icon: 'ri-palette-line', title: '繽紛色彩', desc: '多種色彩選擇，讓孩子愛不釋手' },
  '大功率': { icon: 'ri-volume-up-line', title: '大功率輸出', desc: '最大增益達 82dB，滿足重度聽損需求' },
  '重度聽損': { icon: 'ri-volume-up-line', title: '重度聽損適用', desc: '專為重度聽損設計，強力補償' },
  '反饋抑制': { icon: 'ri-volume-mute-line', title: '反饋抑制', desc: '先進技術消除惱人嘯叫聲' },
  '簡易操作': { icon: 'ri-star-smile-line', title: '簡易操作', desc: '直覺操作，無需複雜設定' },
  '一鍵': { icon: 'ri-toggle-line', title: '一鍵操作', desc: '長按開關、短按切換，簡單好用' },
  '經濟實惠': { icon: 'ri-money-cny-circle-line', title: '高性價比', desc: '價格親民，功能不打折扣' },
};

function SectionReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`scroll-reveal ${isVisible ? 'scroll-reveal-visible' : ''} ${delay > 0 ? `scroll-reveal-delay-${Math.min(delay, 6)}` : ''} ${className}`}
    >
      {children}
    </div>
  );
}

export default function ProductFeatures({ tags }: ProductFeaturesProps) {
  const features = tags
    .map((tag) => featureMap[tag])
    .filter(Boolean);

  if (features.length === 0) return null;

  return (
    <SectionReveal>
      <div className="bg-[#FAF8F5] rounded-2xl border border-[#E8E0D6] p-6 md:p-10">
        <div className="flex items-center gap-2 mb-6 md:mb-8">
          <div className="w-8 h-8 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-sparkling-line text-[#1B3D2A]" />
            </div>
          </div>
          <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">Features</span>
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-[#1B3D2A] mb-6 md:mb-8">
          產品功能特色
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {features.map((feature, i) => (
            <div
              key={feature.title}
              className="bg-white rounded-xl border border-[#E8E0D6]/60 p-5 md:p-6 hover:border-[#D4A574]/50 hover:shadow-sm transition-all duration-300"
              style={{ transitionDelay: `${i * 0.05}s` }}
            >
              <div className="w-10 h-10 rounded-xl bg-[#1B3D2A]/8 flex items-center justify-center mb-4">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${feature.icon} text-[#1B3D2A] text-lg`} />
                </div>
              </div>
              <h3 className="text-base font-bold text-[#1B3D2A] mb-2">
                {feature.title}
              </h3>
              <p className="text-sm text-[#5A6B5A] leading-relaxed">
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </SectionReveal>
  );
}